import Tag from './Tag';
import React from 'react';

interface TagItem {
  id: number;
  label: string;
  FormattedText: () => React.JSX.Element;
  fullText?: string;
  bgColor?: string;
  href?: string;
}

interface TagsProps {
  tags: TagItem[];
  size?: 'small' | 'medium';
  tagType?: 'list' | 'integration' | 'tag';
  className?: string;
  max?: number;
  sx?: React.CSSProperties;
}

interface TagGroupProps {
  children: React.ReactNode;
  max?: number;
  className?: string;
}

const TagGroup: React.FC<TagGroupProps> = ({ children, max, className = '' }) => {
  const childArray = React.Children.toArray(children);
  const visibleChildren = max ? childArray.slice(0, max) : childArray;
  const surplus = max && childArray.length > max ? childArray.length - max : 0;

  return (
    <div
      className={`flex items-center -space-x-5 justify-end ${className}`}
      data-testid="tag-group"
    >
      {visibleChildren}
      {surplus > 0 && (
        <div
          className="h-auto rounded-2xl rounded-bl-none rounded-tr-none text-xs font-semibold border border-dotted border-black bg-white -ml-8 px-2"
          data-testid="tag-surplus"
        >
          +{surplus}
        </div>
      )}
    </div>
  );
};

const Tags: React.FC<TagsProps> = ({
  tags,
  max,
  size = 'small',
  tagType = 'list',
  className,
}) => {
  const tagsWithNoEmptyValues = tags?.filter((tag) => tag);

  if (!tagsWithNoEmptyValues) return null;

  const tagSet = tagsWithNoEmptyValues.map((tag, i) => (
    <Tag tag={tag} size={size} key={tag.id || i} />
  ));

  return tagType === 'tag' ? (
    <div className={`flex ${className || ''}`} data-testid="tags-container">
      {tagSet}
    </div>
  ) : (
    <TagGroup max={max} className={className}>
      {tagSet}
    </TagGroup>
  );
};

export default Tags;
